<template>
    <ui-container class="space-y-8 py-8">

        <!-- Header. -->
        <ui-header :title="item.name" :subtitle="item.categories_string" :image="item.image">
            <template #actions>
                <ui-button-new icon="fas fa-pencil" @click="edit()">Edit</ui-button-new>
                <ui-button-new icon="fas fa-trash" danger @click="openDeleteModal">Delete</ui-button-new>
            </template>
        </ui-header>

        <ui-tabs active="overview" :tabs="tabs" #default="{ isActive }">
            <!-- Overview. -->
            <div v-if="isActive('overview')">
                <ui-data-grid>
                    <ui-data-grid-item label="Categories" :value="item.categories_string"></ui-data-grid-item>
                    <ui-data-grid-item label="Manufacturer" :value="item.has_manufacturer ? item.manufacturer.name : null"></ui-data-grid-item>
                    <ui-data-grid-item label="Unit Of Measure" :value="item.unit_of_measure"></ui-data-grid-item>
                    <ui-data-grid-item label="Form/Shape/Size" :value="item.shape"></ui-data-grid-item>
                    <ui-data-grid-item label="Weight" :value="item.weight ? `${item.weight}kg` : null"></ui-data-grid-item>
                    <ui-data-grid-item label="Additional Information" :value="item.description"></ui-data-grid-item>
                    <ui-data-grid-item wide label="Buying and Selling">
                        <ui-boolean-list :booleans="buyingAndSellingBooleans"></ui-boolean-list>
                    </ui-data-grid-item>

                  <ui-data-grid-item label="Created By">{{ item.created_by }} on {{ item.created_at }}</ui-data-grid-item>
                  <ui-data-grid-item label="Last Updated By">{{ item.updated_by }} on {{ item.updated_at }}</ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Purchase Prices. -->
            <div v-else-if="isActive('purchase_prices')">
                <ui-data-grid class="mb-6">
                    <ui-data-grid-item label="Default Purchase Account Code" :value="item.default_purchase_ac"></ui-data-grid-item>
                    <ui-data-grid-item wide label="Suppliers">
                        <ui-mini-crud
                            :fetch="() => item.suppliers.data"
                            :key="item.key()"
                            add-button-text="New Supplier"
                            empty-state-text="This material has no supplier. Click the button below to add one."
                        >
                            <template #show="{ item, select, removeItem }">
                                <ui-mini-crud-card
                                    :title="item.supplier"
                                    :subtitle="item.getSubtitleArray()"
                                    @edit="select"
                                    @delete="deleteMaterialSupplier(item, removeItem)"
                                ></ui-mini-crud-card>
                            </template>
                            <template #edit="{ item: supplier, unselect }">
                                <assets-materials-suppliers-edit
                                    :material="item"
                                    :item="supplier"
                                    :close="unselect"
                                ></assets-materials-suppliers-edit>
                            </template>
                            <template #create="{ addItem, selectItem, setCreateMode }">
                                <assets-materials-suppliers-create
                                    :material="item"
                                    :close="() => setCreateMode(false)"
                                    :add-item="addItem"
                                    :select-item="selectItem"
                                ></assets-materials-suppliers-create>
                            </template>
                        </ui-mini-crud>
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Sales Prices. -->
            <div v-else-if="isActive('sales_prices')">
                <ui-data-grid>
                    <ui-data-grid-item label="Sale Description" :value="item.sale_description"></ui-data-grid-item>
                    <ui-data-grid-item label="Unit Of Measure" :value="item.unit_of_measure"></ui-data-grid-item>
                    <ui-data-grid-item label="Quantity Of Sale" :value="item.quantity_of_sale"></ui-data-grid-item>
                    <ui-data-grid-item label="Sale Cost">{{ item.sale_cost | toCurrency }}</ui-data-grid-item>
                    <ui-data-grid-item label="Default Sales Account Code" :value="item.default_sale_ac"></ui-data-grid-item>
                    <ui-data-grid-item label="Last Updated" :value="item.updated_at"></ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Stock Control. -->
            <div v-else-if="isActive('stock_control')">
                <ui-data-grid>
                    <ui-data-grid-item label="Amount In Stock" :value="item.stock"></ui-data-grid-item>
                    <ui-data-grid-item label="SKU" :value="item.sku"></ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Files. -->
            <div v-else-if="isActive('files')">
                <filesystem
                    compact="true"
                    :filesystem-id="item.filesystem_id"
                ></filesystem>
            </div>

            <!-- QR Code. -->
            <div v-else-if="isActive('qr_code')">
                <img class="max-w-sm mx-auto my-4" :src="item._links.frontend.qr" />
                <div class="mt-4 text-center">
                    <a :href="item._links.frontend.qr" target="_blank">
                        <ui-button-new primary icon="fas fa-print">Click To Print</ui-button-new>
                    </a>
                    <label class="font-bold mt-8 block">QR Lookup URL</label>
                    <input onClick="this.setSelectionRange(0, this.value.length)" class="max-w-sm mx-auto block" readonly :value="item._links.frontend.lookup" />
                </div>
            </div>
        </ui-tabs>

    </ui-container>
</template>

<script>
export default {
    props: {
        item: Object,
        edit: Function,
        removeItem: Function,
    },
    computed: {
        tabs () {
            return [
                { value: 'overview', label: 'Overview' },
                { value: 'purchase_prices', label: 'Purchase Prices', when: this.item.purchasable },
                { value: 'sales_prices', label: 'Sales Prices', when: this.item.sellable },
                { value: 'stock_control', label: 'Stock Control', when: this.item.stock_control },
                { value: 'files', label: 'Files' },
                { value: 'qr_code', label: 'QR Code' },
            ]
        },
        buyingAndSellingBooleans () {
            return [
                { value: this.item.purchasable, label: 'We Purchase?' },
                { value: this.item.sellable, label: 'We Sell?' },
                { value: this.item.stock_control, label: 'Stock Control' },
            ]
        },
    },
    methods: {
        openDeleteModal () {
            const title = 'Are you sure you want to delete "' + this.item.name + '"?'
            const description = 'Deleting this material is irreversible. Are you sure you want to delete it?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.item.delete()
                this.removeItem(this.item)

            });
        },
        async deleteMaterialSupplier (materialSupplier, removeItem) {
            await materialSupplier.delete()
            removeItem(materialSupplier)
        },
    },
}
</script>
